import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ACCEPTED_CONTENT_TYPE, MAX_FILE_BYTES, VANITY_MAX_LENGTH, validateEmail, validateVanitySlug } from "@mycv/shared";
import { getUploadUrl, putFile, TakenError } from "../lib/api.js";

/** Read ?claim=<slug> from the URL, if present. Returns lowercased + length-clamped. */
function readClaimParam(): string {
  if (typeof window === "undefined") return "";
  const claim = new URLSearchParams(window.location.search).get("claim") ?? "";
  return claim.toLowerCase().slice(0, VANITY_MAX_LENGTH);
}

/** Strip ?claim= from the URL bar so a refresh doesn't re-prefill. */
function consumeClaimParam(): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  if (!params.has("claim")) return;
  params.delete("claim");
  const qs = params.toString();
  const newUrl = window.location.pathname + (qs ? "?" + qs : "") + window.location.hash;
  window.history.replaceState({}, "", newUrl);
}

type Phase =
  | { kind: "idle" }
  | { kind: "preparing" }
  | { kind: "uploading"; progress: number; shareUrl: string }
  | { kind: "ready"; shareUrl: string }
  | { kind: "error"; message: string };

export function UploadZone() {
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [dragActive, setDragActive] = useState(false);
  const [customId, setCustomId] = useState<string>(() => readClaimParam());
  const [prefilledFromUrl, setPrefilledFromUrl] = useState<boolean>(() => readClaimParam() !== "");
  const [uploaderEmail, setUploaderEmail] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  const emailError = useMemo(() => {
    if (!uploaderEmail) return null;
    return validateEmail(uploaderEmail);
  }, [uploaderEmail]);

  // Clear ?claim= from the URL bar after first read so a refresh doesn't re-prefill.
  useEffect(() => { consumeClaimParam(); }, []);

  const customIdError = useMemo(() => {
    if (!customId) return null;
    return validateVanitySlug(customId);
  }, [customId]);

  const startUpload = useCallback(async (file: File) => {
    if (file.type !== ACCEPTED_CONTENT_TYPE) {
      setPhase({ kind: "error", message: "Only PDF files are accepted." });
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setPhase({ kind: "error", message: "File is too large. Max 10 MB." });
      return;
    }
    if (customId && customIdError) {
      setPhase({ kind: "error", message: `Custom link: ${customIdError}` });
      return;
    }
    if (uploaderEmail && emailError) {
      setPhase({ kind: "error", message: `Email: ${emailError}` });
      return;
    }

    setPhase({ kind: "preparing" });
    try {
      const { uploadUrl, shareUrl } = await getUploadUrl({
        filename: file.name,
        contentType: file.type,
        ...(customId ? { customId: customId.trim().toLowerCase() } : {}),
        ...(uploaderEmail ? { uploaderEmail: uploaderEmail.trim().toLowerCase() } : {}),
      });
      setPhase({ kind: "uploading", progress: 0, shareUrl });
      await putFile(uploadUrl, file, ({ loaded, total }) => {
        setPhase({ kind: "uploading", progress: total ? loaded / total : 0, shareUrl });
      });
      setPhase({ kind: "ready", shareUrl });
    } catch (e) {
      if (e instanceof TakenError) {
        setPhase({ kind: "error", message: `"${customId}" is taken. Try a different name.` });
      } else {
        setPhase({ kind: "error", message: e instanceof Error ? e.message : "upload failed" });
      }
    }
  }, [customId, customIdError, uploaderEmail, emailError]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void startUpload(file);
  }, [startUpload]);

  const onPick = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void startUpload(file);
  }, [startUpload]);

  const reset = () => {
    setPhase({ kind: "idle" });
    setCustomId("");
    setUploaderEmail("");
  };

  const isInteractive = phase.kind === "idle" || phase.kind === "error";

  return (
    <section className="upload">
      <div className="hero">
        <div className="badge">Real-time tracking · Push notifications · Cool link</div>
        <h1>Track who's reading your resume.</h1>
        <p className="lede">Upload a PDF. Share one permanent link. Get a notification the moment someone opens it.</p>
      </div>

      {isInteractive && (
        <div className="vanity">
          <label htmlFor="uploader-email" className="vanity__label">
            Want to see who opens your resume? <span className="vanity__optional">(optional)</span>
          </label>
          <div className={`vanity__field ${emailError ? "vanity__field--err" : ""}`}>
            <input
              id="uploader-email"
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              autoCapitalize="off"
              spellCheck={false}
              value={uploaderEmail}
              onChange={(e) => setUploaderEmail(e.target.value)}
              maxLength={254}
              style={{ paddingLeft: "0.85rem" }}
            />
          </div>
          {uploaderEmail && emailError && (
            <div className="vanity__err">⚠ {emailError}</div>
          )}
          {uploaderEmail && !emailError && (
            <div className="vanity__ok">✓ we'll email you a sign-in link for your <a href="/analytics" style={{ color: "var(--accent)" }}>analytics</a></div>
          )}
          {!uploaderEmail && (
            <div className="vanity__hint">We'll email a sign-in link so you can view analytics later. Leave blank to upload without it.</div>
          )}
        </div>
      )}

      {isInteractive && (
        <div className="vanity">
          <label htmlFor="custom-id" className="vanity__label">
            Want a custom link? <span className="vanity__optional">(optional)</span>
          </label>
          <div className={`vanity__field ${customIdError ? "vanity__field--err" : ""}`}>
            <span className="vanity__prefix">mycv.now/r/</span>
            <input
              id="custom-id"
              type="text"
              placeholder="your-name"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              value={customId}
              onChange={(e) => { setCustomId(e.target.value.toLowerCase()); setPrefilledFromUrl(false); }}
              maxLength={32}
              autoFocus={prefilledFromUrl}
            />
          </div>
          {prefilledFromUrl && customId && (
            <div className="vanity__hint">↳ pre-filled from <code>?claim=…</code></div>
          )}
          {customId && customIdError && (
            <div className="vanity__err">⚠ {customIdError}</div>
          )}
          {customId && !customIdError && phase.kind !== "error" && (
            <div className="vanity__ok">✓ looks good — first to claim wins</div>
          )}
          {customId && !customIdError && phase.kind === "error" && (
            <div className="vanity__err">⚠ {phase.message}</div>
          )}
          {!customId && (
            <div className="vanity__hint">Leave blank for a random short code.</div>
          )}
        </div>
      )}

      <div
        className={`drop drop--${phase.kind} ${dragActive ? "drop--drag" : ""}`}
        onDragOver={(e) => { e.preventDefault(); if (isInteractive) setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => isInteractive && onDrop(e)}
        onClick={() => isInteractive && inputRef.current?.click()}
        role={isInteractive ? "button" : undefined}
        tabIndex={isInteractive ? 0 : -1}
        aria-disabled={!isInteractive}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          onChange={onPick}
          hidden
        />

        {(phase.kind === "idle" || phase.kind === "error") && (
          <>
            <FileIcon />
            <div className="drop__title">
              {phase.kind === "error" ? "Try again" : "Drop your PDF here"}
            </div>
            <div className="drop__sub">
              {phase.kind === "error" ? phase.message : "or click to browse — PDF only, up to 10 MB"}
            </div>
          </>
        )}

        {phase.kind === "preparing" && (
          <>
            <Spinner />
            <div className="drop__title">Preparing your link…</div>
          </>
        )}

        {phase.kind === "uploading" && (
          <>
            <div className="drop__title">Uploading {Math.round(phase.progress * 100)}%</div>
            <progress value={phase.progress} max={1} />
            <ShareLink url={phase.shareUrl} hint="Your link is ready to copy. The PDF is finishing in the background." />
          </>
        )}

        {phase.kind === "ready" && (
          <>
            <CheckIcon />
            <div className="drop__title">Cool link ready</div>
            <ShareLink url={phase.shareUrl} hint="" />
            <button className="btn btn--ghost" onClick={(e) => { e.stopPropagation(); reset(); }}>
              Upload another
            </button>
          </>
        )}
      </div>

    </section>
  );
}

function ShareLink({ url, hint }: { url: string; hint: string }) {
  const [copied, setCopied] = useState(false);
  const copy = (e: React.MouseEvent) => {
    e.stopPropagation();
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };
  return (
    <div className="share" onClick={(e) => e.stopPropagation()}>
      <div className="share__url">
        <a href={url} target="_blank" rel="noreferrer">{url}</a>
      </div>
      <button className="btn btn--primary" onClick={copy}>
        {copied ? "Copied ✓" : "Copy link"}
      </button>
      {hint && <small className="share__hint">{hint}</small>}
    </div>
  );
}

// ─── Icons (inline SVG, no deps) ─────────────────────────────────────────────

function FileIcon() {
  return (
    <svg className="drop__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="9" y1="13" x2="15" y2="13" />
      <line x1="9" y1="17" x2="15" y2="17" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="drop__icon drop__icon--ok" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function Spinner() {
  return (
    <div className="spinner" aria-hidden="true">
      <div /><div /><div /><div />
    </div>
  );
}
